const CUSTOM_ELEMENT_NAME = "i18n-text";
const I18N_DICT = {};
const ELEMENT_MAP = new Map(); // key -> Set of elements using that key

// Regex for template processing
const VARS_RE = /\{\{([^}]+)\}\}/g;
const NEST_RE = /\[\[([^\]]+)\]\]/g;
// Check if string contains HTML tags
const HTML_RE = /<[^>]+>/g;

// Dependency tracking for nested keys
const DEPENDENCY_GRAPH = new Map(); // key -> Set of keys that depend on it
const PROCESSED_CACHE = new Map(); // key -> processed template
const TEMPLATE_CACHE = new Map(); // key -> { data, variableNames }

// Element map management
const addElementToMap = (key, element) => {
  if (!key) return;
  if (!ELEMENT_MAP.has(key)) ELEMENT_MAP.set(key, new Set());
  ELEMENT_MAP.get(key).add(element);
};

const removeElementFromMap = (key, element) => {
  if (!key) return;
  const elements = ELEMENT_MAP.get(key);
  if (!elements) return;
  elements.delete(element);
  if (elements.size === 0) ELEMENT_MAP.delete(key);
};

const updateElementsForKey = (key) => {
  // Update all elements using this key
  ELEMENT_MAP.get(key)?.forEach((el) => el.update());
  // Recursively update dependent keys
  DEPENDENCY_GRAPH.get(key)?.forEach(updateElementsForKey);
};

const invalidateCache = (key) => {
  // Clear all caches
  PROCESSED_CACHE.delete(key);
  TEMPLATE_CACHE.delete(key);

  // Recursively invalidate dependents (do this before deleting from graph)
  DEPENDENCY_GRAPH.get(key)?.forEach(invalidateCache);

  // Prune from dependency graph (will be rebuilt lazily on next use)
  DEPENDENCY_GRAPH.delete(key);
  for (const [, dependentSet] of DEPENDENCY_GRAPH) dependentSet.delete(key);
};

const toNested = (template, invokers = []) => {
  const currentKey = invokers[invokers.length - 1];
  if (currentKey && PROCESSED_CACHE.has(currentKey)) return PROCESSED_CACHE.get(currentKey);

  const replacer = (fallback, group1) => {
    // Circular reference check
    if (invokers.includes(group1)) {
      console.error(`[i18n-text] Circular replacement: ${[...invokers, group1].join(" -> ")}`);
      return fallback;
    }

    const nested = I18N_DICT[group1];
    // Validation checks with early returns
    if (!nested) {
      console.error(`[i18n-text] Unknown key: ${group1}`);
      return fallback;
    }
    if (nested.match(VARS_RE)) {
      console.error(`[i18n-text] Nested keys cannot contain vars: ${group1}`);
      return nested;
    }
    if (nested.match(NEST_RE)) {
      console.error(`[i18n-text] Nested keys cannot nest: ${group1}`);
      return nested;
    }

    // Track dependency
    if (currentKey) {
      if (!DEPENDENCY_GRAPH.has(group1)) DEPENDENCY_GRAPH.set(group1, new Set());
      DEPENDENCY_GRAPH.get(group1).add(currentKey);
    }

    return toNested(nested, [...invokers, group1]);
  };

  const result = template.replace(NEST_RE, replacer);
  if (currentKey) PROCESSED_CACHE.set(currentKey, result);
  return result;
};

const isObject = (maybeObject) =>
  typeof maybeObject === "object" && maybeObject !== null && maybeObject.constructor === Object;

// Extract all variable names from a template string. Used once during analyzeTemplate.
const extractVariableNames = (template) => {
  const names = new Set();
  let match;
  VARS_RE.lastIndex = 0;
  while ((match = VARS_RE.exec(template)) !== null) names.add(match[1].trim());
  return names;
};

/**
 * Analyze template and cache rendering metadata.
 * Simplified to two cases: simple (text only) vs complex (has variables or HTML).
 * Variable names are extracted once here, not during render.
 * @returns {{ data: string, variableNames: Set<string>, hasHtml: boolean }}
 */
const analyzeTemplate = (template) => {
  HTML_RE.lastIndex = 0;
  return {
    data: template,
    variableNames: extractVariableNames(template),
    hasHtml: HTML_RE.test(template),
  };
};

// Collect all text nodes from a DOM tree
const getTextNodes = (node) => {
  const nodes = [];
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);
  let current;
  while ((current = walker.nextNode())) nodes.push(current);
  return nodes;
};

// Slot helpers
const getSlotName = (slot) => slot.getAttribute("slot");
const setHidden = (hidden) => (slot) => {
  slot.hidden = hidden;
  return slot;
};
const hideSlot = setHidden(true);
const showSlot = setHidden(false);

// Build slot name -> element map
const buildSlotMap = (slots) =>
  slots.reduce((map, slot) => {
    const name = getSlotName(slot);
    if (name) map[name] = slot;
    return map;
  }, {});

// Partition array into [matches, nonMatches] based on predicate
const partition = (predicate) => (arr) =>
  arr.reduce((acc, item) => {
    acc[predicate(item) ? 0 : 1].push(item);
    return acc;
  }, [[], []]);

const textNode = (str) => document.createTextNode(str);

// Replace text node containing {{vars}} with text nodes and slot elements
const processTextNode = (slotMap) => (node) => {
  if (!node.textContent.includes("{{")) return;

  const parent = node.parentNode;
  if (!parent) return;

  // Build replacement nodes: split on {{var}} and interleave with slots
  let lastIndex = 0;
  let match;
  VARS_RE.lastIndex = 0;

  while ((match = VARS_RE.exec(node.textContent)) !== null) {
    // Text before the variable
    if (match.index > lastIndex) {
      parent.insertBefore(textNode(node.textContent.slice(lastIndex, match.index)), node);
    }
    // The slot element (or fallback text if slot missing)
    const varName = match[1].trim();
    const slot = slotMap[varName];
    parent.insertBefore(slot ? showSlot(slot) : textNode(match[0]), node);
    lastIndex = VARS_RE.lastIndex;
  }

  // Text after the last variable
  if (lastIndex < node.textContent.length) {
    parent.insertBefore(textNode(node.textContent.slice(lastIndex)), node);
  }

  parent.removeChild(node);
};

const attrCallbackCases = {
  "translation-key": (el, oldVal, newVal) => {
    removeElementFromMap(oldVal, el);
    el.key = newVal;
    addElementToMap(newVal, el);
    el.update();
  },
  "translation-fallback": (el, _oldVal, newVal) => {
    el.fallback = newVal;
    // Invalidate caches when fallback changes (template source changed)
    if (el.key) invalidateCache(el.key);
    el.update();
  },
};

const init = () => {
  customElements.define(
    CUSTOM_ELEMENT_NAME,
    class extends HTMLElement {
      constructor() {
        super();
      }

      // Dictionary management methods
      getDict() {
        return { ...I18N_DICT };
      }
      clearDict() {
        Object.keys(I18N_DICT).forEach((key) => delete I18N_DICT[key]);
        DEPENDENCY_GRAPH.clear();
        PROCESSED_CACHE.clear();
        TEMPLATE_CACHE.clear();
        return { ...I18N_DICT };
      }
      mergeDict(newDict) {
        if (!isObject(newDict)) return { ...I18N_DICT };

        // Collect changed keys, update dict, then invalidate caches and update elements
        const changed = Object.entries(newDict)
          .filter(([k, v]) => I18N_DICT[k] !== v)
          .map(([k, v]) => {
            I18N_DICT[k] = v;
            return k;
          });

        changed.forEach(invalidateCache);
        changed.forEach(updateElementsForKey);
        return { ...I18N_DICT };
      }
      setDict(newDict) {
        this.clearDict();
        return this.mergeDict(newDict);
      }

      // Render template: simple (no vars) or withVars (unified strategy using innerHTML)
      async update() {
        // Wait for DOM to settle (Elm uses rAF, we use microtask to run before next frame)
        await Promise.resolve();
        if (!this.key) return;

        const templateString = toNested(I18N_DICT[this.key] || this.fallback || "", [this.key]);
        if (templateString === this._processedTemplate) return this;
        this._processedTemplate = templateString;

        // Get or create cached template analysis.
        // Re-analyze if cached data doesn't match (e.g., fallback changed).
        let cached = TEMPLATE_CACHE.get(this.key);
        if (!cached || cached.data !== templateString) {
          cached = analyzeTemplate(templateString);
          TEMPLATE_CACHE.set(this.key, cached);
        }
        // Use querySelectorAll to find slots at any depth. After the first
        // render, slots may be nested inside template HTML (e.g., inside
        // <strong> from "<strong>{{1}}</strong>"). this.children only returns
        // direct children and would miss these nested slots, causing them to
        // be lost when innerHTML is overwritten.
        const slots = [...this.querySelectorAll("[slot]")];

        // Simple: plain text with no variables and no HTML
        if (cached.variableNames.size === 0 && !cached.hasHtml) {
          slots.forEach(hideSlot);
          this.replaceChildren(textNode(cached.data), ...slots);
          return this;
        }

        const isUsed = (slot) => cached.variableNames.has(getSlotName(slot));
        const [usedSlots, unusedSlots] = partition(isUsed)(slots);
        const slotMap = buildSlotMap(usedSlots);
        const hiddenSlots = unusedSlots.map(hideSlot);

        // WithVars: parse HTML, walk text nodes, replace {{vars}} with slot elements
        this.innerHTML = cached.data;
        getTextNodes(this).forEach(processTextNode(slotMap));
        this.append(...hiddenSlots);
        return this;
      }

      // Lifecycle
      connectedCallback() {
        addElementToMap(this.key, this);
        this.update();
      }
      disconnectedCallback() {
        removeElementFromMap(this.key, this);
      }
      static get observedAttributes() {
        return Object.keys(attrCallbackCases);
      }
      attributeChangedCallback(name, oldVal, newVal) {
        attrCallbackCases[name]?.(this, oldVal, newVal);
      }
    },
  );
};

export default { init };
